// Renders one dashboard purely from its resolved schema: filters → widgets via the registry.
// Filter state lives in the URL so any view can be shared or bookmarked.
import { useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useDashboardQuery, useSavePrefsMutation, errorMessage } from '../app/api';
import type { DashboardSchema, Shell } from '../app/types';
import { AppShell } from './AppShell';
import { FilterBar, type FilterValues } from './FilterBar';
import { CustomizeDialog, ViewMenu } from './ViewMenu';
import { Icon } from '../ui/Icon';
import { WidgetFrame } from './WidgetFrame';
import { useToast } from '../ui/Toasts';
import { rememberChoice, rememberedChoice } from './sticky';

function decode(raw: string | null, type: string): unknown {
  if (raw === null) return undefined;
  if (type === 'date-range' && raw.startsWith('custom:')) {
    const [, start, end] = raw.split(':');
    return { preset: 'custom', start, end };
  }
  if (type === 'store-select' && raw !== 'all' && raw.includes(',')) return raw.split(',');
  if (type === 'store-select' && raw !== 'all') return [raw];
  return raw;
}

function encode(v: unknown): string {
  if (Array.isArray(v)) return v.join(',');
  if (v && typeof v === 'object') return `custom:${(v as any).start}:${(v as any).end}`;
  return String(v);
}

function defaultFor(f: DashboardSchema['filters'][number], shell: Shell): unknown {
  if (f.type === 'store-select') {
    if (!f.multiple) return f.default && f.default !== 'first' && shell.stores.some((s) => s.id === f.default) ? f.default : shell.stores[0]?.id;
    return f.default ?? 'all';
  }
  return f.default;
}

export function useDashboardFilters(schema: DashboardSchema | undefined, shell: Shell) {
  const [params, setParams] = useSearchParams();
  const values = useMemo(() => {
    const v: FilterValues = {};
    for (const f of schema?.filters ?? []) {
      let x = decode(params.get(f.id), f.type);
      if (f.type === 'store-select' && !f.multiple && Array.isArray(x)) x = x[0];
      v[f.id] = x ?? rememberedChoice(f) ?? defaultFor(f, shell);
    }
    return v;
  }, [schema, params, shell]);
  const set = (id: string, v: unknown) => {
    rememberChoice(schema?.filters.find((f) => f.id === id), v);
    setParams(
      (p) => {
        const n = new URLSearchParams(p);
        n.set(id, encode(v));
        return n;
      },
      { replace: true },
    );
  };
  return [values, set] as const;
}

export function DashboardPage({ shell }: { shell: Shell }) {
  const { dashboardId = '' } = useParams();
  const [params] = useSearchParams();
  const toast = useToast();
  const [savePrefs] = useSavePrefsMutation();
  // The store filter (single-store dashboards) selects the Layer-3 store override server-side.
  const storeParam = params.get('store');
  const { data: schema, error, isLoading } = useDashboardQuery({ id: dashboardId, store: storeParam ?? undefined });
  const [values, setValue] = useDashboardFilters(schema, shell);
  const storeScope = schema?.filters.some((f) => f.id === 'store') ? (values.store as string) : undefined;
  const { data: scoped } = useDashboardQuery({ id: dashboardId, store: storeScope }, { skip: !storeScope || storeScope === storeParam });
  const active = scoped ?? schema;

  const [customizing, setCustomizing] = useState(false);
  const hidden = active?.hiddenWidgets ?? [];
  const hints = (active?.filters ?? [])
    .map((f) => f.options?.find((o) => o.value === values[f.id])?.hint)
    .filter((h): h is string => !!h);
  const saveDefaults = async () => {
    try {
      await savePrefs({ dashboardId, filterDefaults: values }).unwrap();
      toast('Saved as your default filters', 'success');
    } catch (e) {
      toast(errorMessage(e), 'error');
    }
  };
  // While the dashboard loads, its filter row keeps its place so the page doesn't jump when it arrives.
  const toolbar = isLoading ? <div className="skeleton toolbar-skeleton" aria-hidden /> : active && active.filters.length > 0 ? <FilterBar filters={active.filters} values={values} shell={shell} onChange={setValue} /> : null;
  const actions = active ? (
    <ViewMenu dashboard={active} shell={shell} canSaveFilters={active.filters.length > 0} onSaveDefaults={saveDefaults} onCustomize={() => setCustomizing(true)} />
  ) : null;

  return (
    <AppShell shell={shell} title={active?.title ?? ''} toolbar={toolbar} actions={actions}>
      {isLoading ? (
        <div className="grid">
          {[3, 3, 3, 3, 8, 4].map((w, i) => (
            <div key={i} className="widget loading" style={{ ['--w12' as any]: w, ['--w8' as any]: w > 4 ? 8 : 4, ['--w4' as any]: 4, ['--h' as any]: i < 4 ? 1 : 3 }}>
              <div className="skeleton" style={{ margin: 18, flex: 1 }} />
            </div>
          ))}
        </div>
      ) : error || !active ? (
        <div className="state" style={{ marginTop: '12vh' }}>
          <strong>Dashboard unavailable</strong>
          {errorMessage(error)}
        </div>
      ) : (
        <div dir={active.direction}>
          {hints.map((h) => (
            <div className="page-note" role="note" key={h}>
              <Icon name="info" size={14} />
              <span>{h}</span>
            </div>
          ))}
          {hidden.length > 0 && (
            <div className="hidden-bar" role="status">
              <Icon name="eyeOff" size={14} />
              <span className="hidden-bar-text">
                {hidden.length} hidden widget{hidden.length > 1 ? 's' : ''}: {hidden.map((w) => w.title).join(', ')}
              </span>
              <button className="btn sm" onClick={() => savePrefs({ dashboardId: active.id, resetHidden: true })}>
                Show all
              </button>
              <button className="btn sm ghost" onClick={() => setCustomizing(true)}>
                Choose…
              </button>
            </div>
          )}
          <div className="grid">
            {active.widgets.map((w, i) => (
              <WidgetFrame key={`${w.id}:${active.version}`} index={i} widget={w} dashboardId={active.id} filters={values} shell={shell} />
            ))}
          </div>
          {active.widgets.length === 0 && (
            <div className="state">{hidden.length ? 'All widgets on this page are hidden — use “Show all” above.' : 'This dashboard has no widgets yet. Add some in Settings → Configuration → Dashboard builder.'}</div>
          )}
          {customizing && <CustomizeDialog dashboard={active} onClose={() => setCustomizing(false)} />}
        </div>
      )}
    </AppShell>
  );
}
